export const API_BASE = import.meta.env.VITE_API_BASE || 'https://api.3dsmart.vn/api'; // Hoặc đường dẫn API thực tế của bạn trên Cloudflare

export type AuthUser = {
  id: string;
  email: string;
  role: 'ADMIN' | 'USER';
  name?: string | null;
  address?: string | null;
  phone?: string | null;
  createdAt?: string;
};

export type BackendProduct = {
  id: string;
  name: string;
  slug: string;
  priceDisplay: string;
  description: string;
  ecommerceLink: string;
  buttonText?: string | null;
  isActive: boolean;
  images?: string | null;
  features?: string | null;
  specs?: string | null;
  createdAt?: string;
  updatedAt?: string;
};

export type BackendPost = {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  category: string;
  coverImage?: string | null;
  publishedAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
};

export type BackendFirmware = {
  id: string;
  name: string;
  version: string;
  model: string;
  variant?: string | null;
  fileUrl: string;
  checksum: string;
  isActive: boolean;
  releaseNote?: string | null;
  createdAt?: string;
  updatedAt?: string;
};

export type DeviceActivationPayload = {
  deviceUid: string;
  model: string;
  source?: 'SHOP' | 'EXTERNAL_APPROVED';
};

export function getAuthToken() {
  return localStorage.getItem('auth_token') || '';
}

export function setAuthToken(token: string) {
  localStorage.setItem('auth_token', token);
}

export function clearAuthToken() {
  localStorage.removeItem('auth_token');
  localStorage.removeItem('auth_user');
  window.dispatchEvent(new Event(AUTH_USER_CHANGED_EVENT));
}

export const AUTH_SESSION_EXPIRED_EVENT = 'auth-session-expired';
export const AUTH_USER_CHANGED_EVENT = 'auth-user-changed';

export function getAuthUser(): AuthUser | null {
  try {
    const data = localStorage.getItem('auth_user');
    return data ? JSON.parse(data) : null;
  } catch {
    return null;
  }
}

export function setAuthUser(user: AuthUser) {
  localStorage.setItem('auth_user', JSON.stringify(user));
  window.dispatchEvent(new Event(AUTH_USER_CHANGED_EVENT));
}

const apiCache = new Map<string, { data: any; expiry: number }>();

export function getApiCacheSync<T>(path: string, requireToken = false): T | null {
  const cacheKey = requireToken ? `${path}_token` : path;
  const cached = apiCache.get(cacheKey);
  if (cached && cached.expiry > Date.now()) {
    return cached.data as T;
  }
  return null;
}

export function invalidateApiCache(path: string, requireToken = false) {
  const cacheKey = requireToken ? `${path}_token` : path;
  apiCache.delete(cacheKey);
}


export async function apiRequest<T>(path: string, init?: RequestInit & { useCache?: boolean }, requireToken = false): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (init?.headers) {
    const normalized = new Headers(init.headers);
    normalized.forEach((value, key) => {
      headers[key] = value;
    });
  }

  if (requireToken) {
    const token = getAuthToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  }

  const isGet = !init || !init.method || init.method.toUpperCase() === 'GET';
  const useCache = isGet && init?.useCache;
  const cacheKey = requireToken ? `${path}_token` : path;

  if (useCache) {
    const cached = apiCache.get(cacheKey);
    if (cached && cached.expiry > Date.now()) {
      return cached.data as T;
    }
  }

  let finalPath = path;
  if (!useCache && isGet) {
    const separator = path.includes('?') ? '&' : '?';
    finalPath = `${path}${separator}_t=${Date.now()}`;
  }

  const timeoutMs = (init as any)?.timeout ?? 25000;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${finalPath}`, {
      ...init,
      headers,
      signal: init?.signal || controller.signal,
      cache: 'no-store'
    });
  } catch (err: any) {
    if (err.name === 'AbortError') {
      throw new Error('Yêu cầu hết thời gian chờ (Timeout). Vui lòng thử lại.');
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }

  if (!response.ok) {
    let message = `Request failed: ${response.status}`;
    let waitSeconds: number | undefined;
    try {
      const data = (await response.json()) as { message?: string; waitSeconds?: number };
      if (data.message) {
        message = data.message;
      }
      if (data.waitSeconds) {
        waitSeconds = data.waitSeconds;
      }
    } catch {
      // keep fallback message
    }
    if (requireToken && (
      (response.status === 401 && (message === 'Invalid token' || message === 'Unauthorized')) ||
      (response.status === 404 && message === 'User not found')
    )) {
      clearAuthToken();
      window.dispatchEvent(new Event(AUTH_SESSION_EXPIRED_EVENT));
      throw new Error('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.');
    }

    const err = new Error(message) as Error & { waitSeconds?: number };
    if (waitSeconds) err.waitSeconds = waitSeconds;
    throw err;
  }

  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return null as T;
  }

  const data = await response.json();
  if (useCache) {
    apiCache.set(cacheKey, { data, expiry: Date.now() + 60000 }); // cache 60s
  }
  return data as T;
}

export async function tryApiRequest<T>(path: string, init?: RequestInit & { useCache?: boolean }, requireToken = false): Promise<T | null> {
  try {
    return await apiRequest<T>(path, init, requireToken);
  } catch {
    return null;
  }
}

export function isLoggedIn() {
  return Boolean(getAuthToken());
}

export async function uploadFiles(files: File[]): Promise<string[]> {
  const formData = new FormData();
  files.forEach((file) => formData.append('files', file));

  const token = getAuthToken();
  const headers: Record<string, string> = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}/upload`, {
    method: 'POST',
    body: formData,
    headers,
  });

  if (!response.ok) {
    throw new Error('Upload failed');
  }

  const data = await response.json();
  return data.urls;
}
